import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { previewBulkAdmission } from "../../../utils/Api";

const normalizePaymentStatus = (status) =>
  String(status || "unpaid")
    .trim()
    .toLowerCase();

const paymentChipColor = (status) => {
  const normalized = normalizePaymentStatus(status);
  if (normalized === "paid") return "success";
  if (normalized === "partial") return "warning";
  return "error";
};

const formatServerIssue = (item) => {
  if (typeof item === "string") return item;
  return item?.reason || item?.message || item?.code || "Unknown issue";
};

const formatPreviewError = (error) => {
  const data = error?.response?.data;
  const details = [
    ...(Array.isArray(data?.blockers) ? data.blockers : []),
    ...(Array.isArray(data?.errors) ? data.errors : []),
  ]
    .map(formatServerIssue)
    .filter(Boolean);

  if (details.length > 0) return details.join("; ");
  if (typeof data === "string" && data.trim()) return data;
  return data?.message || "Could not build the admission preview.";
};

/**
 * Admit-students dialog.
 *
 * Replaces a dialog that offered a single batch dropdown and nothing else. It could not express
 * what the office actually needed, so the 2026-27 LLM intake had to be done with one-off
 * scripts. What that exposed, and what this now handles:
 *
 *   - PREVIEW. The old dialog committed blind. Every admission here is previewed first: the
 *     exact roll number and GR number each applicant will get, in the order they will get them.
 *   - GR NUMBERS. Nothing in the app ever assigned one; grNumber was whatever the applicant
 *     typed on the form. Now opt-in, with the next register number suggested.
 *   - PAYMENT. The bulk path had no payment check at all. Unpaid applicants are now skipped by
 *     default, and admitting them anyway requires a reason that is stored on the application.
 *   - EMAIL. Mail used to be mandatory, and a failed send DELETED the student. Now it is a
 *     toggle, and sending happens after every record is safely committed.
 *
 * Roll numbers are assigned in surname order and APPENDED to whatever the batch already holds.
 * A second wave therefore continues the sequence rather than interleaving — which is correct,
 * because a number already issued to a student must not silently change. To put the whole batch
 * back into alphabetical order after the last wave, use Roll Number Reconcile.
 */
const AdmitStudentsDialog = ({
  open,
  onClose,
  onConfirm,
  baseBatches,
  applicationIds,
  isSingle,
  submitting,
}) => {
  const [batchName, setBatchName] = useState("");
  const [allowUnpaid, setAllowUnpaid] = useState(false);
  const [paymentOverrideReason, setPaymentOverrideReason] = useState("");
  const [assignGrNumbers, setAssignGrNumbers] = useState(false);
  const [grStart, setGrStart] = useState("");
  const [sendEmail, setSendEmail] = useState(true);

  const [preview, setPreview] = useState(null);
  const [previewKey, setPreviewKey] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const previewRequestId = useRef(0);

  // Reset everything when the dialog is reopened so settings never leak between runs.
  useEffect(() => {
    if (!open) return;
    setBatchName("");
    setAllowUnpaid(false);
    setPaymentOverrideReason("");
    setAssignGrNumbers(false);
    setGrStart("");
    setSendEmail(true);
    setPreview(null);
    setPreviewKey("");
    setPreviewError("");
    previewRequestId.current += 1;
  }, [open]);

  const currentPreviewKey = JSON.stringify({
    applicationIds,
    batchName,
    allowUnpaid,
    paymentOverrideReason,
    assignGrNumbers,
    grStart,
  });

  const runPreview = useCallback(async () => {
    if (!batchName || applicationIds.length === 0) return;
    const requestId = ++previewRequestId.current;
    setPreviewing(true);
    setPreviewError("");
    try {
      const data = await previewBulkAdmission({
        applicationIds,
        baseBatchName: batchName,
        allowUnpaid,
        paymentOverrideReason: paymentOverrideReason.trim(),
        assignGrNumbers,
        grStart: grStart === "" ? null : Number(grStart),
      });
      if (requestId !== previewRequestId.current) return;
      setPreview(data);
      setPreviewKey(currentPreviewKey);
    } catch (error) {
      if (requestId !== previewRequestId.current) return;
      setPreview(null);
      setPreviewKey("");
      setPreviewError(formatPreviewError(error));
    } finally {
      if (requestId === previewRequestId.current) setPreviewing(false);
    }
  }, [
    batchName,
    applicationIds,
    allowUnpaid,
    paymentOverrideReason,
    assignGrNumbers,
    grStart,
    currentPreviewKey,
  ]);

  // Invalidate the old plan as soon as an outcome-changing input changes, then debounce a new
  // preview. The confirm button only enables for the current preview key.
  useEffect(() => {
    previewRequestId.current += 1;
    setPreview(null);
    setPreviewKey("");
    setPreviewError("");
    setPreviewing(false);
    if (!open || !batchName || applicationIds.length === 0) return undefined;

    const timer = setTimeout(runPreview, 350);
    return () => clearTimeout(timer);
  }, [
    open,
    batchName,
    allowUnpaid,
    assignGrNumbers,
    grStart,
    paymentOverrideReason,
    currentPreviewKey,
    runPreview,
    applicationIds.length,
  ]);

  const hasCurrentPreview = !!preview && previewKey === currentPreviewKey;
  const blockers = hasCurrentPreview ? preview.blockers || [] : [];
  const warnings = hasCurrentPreview ? preview.warnings || [] : [];
  const plan = hasCurrentPreview ? preview.plan || [] : [];
  const skipped = hasCurrentPreview ? preview.skipped || [] : [];
  const unpaidOverrideCount = plan.filter(
    (item) => normalizePaymentStatus(item.paymentStatus) === "unpaid",
  ).length;
  const unpaidOverrideRequested = skipped.some((item) =>
    ["PAYMENT_REQUIRED", "PAYMENT_OVERRIDE_REASON_REQUIRED"].includes(
      item.eligibilityCode,
    ),
  );
  const overrideReasonMissing =
    allowUnpaid && unpaidOverrideRequested && !paymentOverrideReason.trim();
  const unpaidPlannedWithoutOverride =
    unpaidOverrideCount > 0 && (!allowUnpaid || !paymentOverrideReason.trim());

  const canAdmit = useMemo(
    () =>
      !!batchName &&
      hasCurrentPreview &&
      !previewing &&
      !submitting &&
      !overrideReasonMissing &&
      !unpaidPlannedWithoutOverride &&
      plan.length > 0 &&
      blockers.length === 0,
    [
      batchName,
      hasCurrentPreview,
      previewing,
      submitting,
      overrideReasonMissing,
      unpaidPlannedWithoutOverride,
      plan.length,
      blockers.length,
    ],
  );

  const handleConfirm = () => {
    if (!canAdmit) return;
    onConfirm({
      baseBatchName: batchName,
      allowUnpaid,
      paymentOverrideReason: paymentOverrideReason.trim(),
      assignGrNumbers,
      grStart: grStart === "" ? null : Number(grStart),
      sendEmail,
      plannedCount: plan.length,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: "10px" } }}
    >
      <DialogTitle
        sx={{
          borderBottom: "1px solid #e2e8f0",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <span>
          {isSingle
            ? "Admit Student"
            : `Admit ${applicationIds.length} Selected Application(s)`}
        </span>
        {preview && hasCurrentPreview && (
          <Chip
            size="small"
            color={blockers.length ? "error" : "success"}
            label={
              blockers.length
                ? `${blockers.length} blocker(s)`
                : `${plan.length} planned`
            }
          />
        )}
      </DialogTitle>

      <DialogContent sx={{ pt: "20px !important" }}>
        {/* ---------------------------------------------------------------- options */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "flex-start",
          }}
        >
          <FormControl sx={{ minWidth: 220 }} margin="dense">
            <InputLabel id="batch-select-label">Batch / Class</InputLabel>
            <Select
              labelId="batch-select-label"
              value={batchName}
              label="Batch / Class"
              onChange={(e) => setBatchName(e.target.value)}
            >
              {baseBatches.map((b) => (
                <MenuItem key={b} value={b}>
                  {b}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: "flex", flexDirection: "column", pt: 0.5 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={assignGrNumbers}
                  onChange={(e) => setAssignGrNumbers(e.target.checked)}
                />
              }
              label="Assign GR numbers"
            />
            {assignGrNumbers && (
              <Tooltip title="Leave blank to continue the programme's existing GR register.">
                <TextField
                  size="small"
                  label="Start GR from"
                  value={grStart}
                  onChange={(e) =>
                    setGrStart(e.target.value.replace(/\D/g, ""))
                  }
                  placeholder={
                    preview?.grSuggestion?.next
                      ? String(preview.grSuggestion.next)
                      : "auto"
                  }
                  sx={{ width: 160, ml: 4 }}
                />
              </Tooltip>
            )}
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", pt: 0.5 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allowUnpaid}
                  onChange={(e) => {
                    setAllowUnpaid(e.target.checked);
                    if (!e.target.checked) setPaymentOverrideReason("");
                  }}
                />
              }
              label="Allow unpaid admission override"
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ ml: 4, maxWidth: 300 }}
            >
              Partial payments are allowed normally. This override applies only
              to unpaid applications.
            </Typography>
            {allowUnpaid && (
              <TextField
                required
                size="small"
                label="Unpaid override reason"
                value={paymentOverrideReason}
                onChange={(e) => setPaymentOverrideReason(e.target.value)}
                placeholder="e.g. payment approved by the office"
                sx={{ width: 300, ml: 4, mt: 1 }}
                error={overrideReasonMissing}
                helperText={
                  overrideReasonMissing
                    ? "A reason is required before unpaid admission can proceed."
                    : "This reason will be recorded with each unpaid admission."
                }
              />
            )}
          </Box>

          <FormControlLabel
            sx={{ pt: 1 }}
            control={
              <Checkbox
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
              />
            }
            label="Email login details to students"
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* ---------------------------------------------------------------- feedback */}
        {preview && hasCurrentPreview && (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              mb: 2,
              alignItems: "center",
            }}
          >
            <Chip
              size="small"
              variant="outlined"
              label={`Selected: ${applicationIds.length}`}
            />
            <Chip
              size="small"
              color="success"
              label={`Planned: ${plan.length}`}
            />
            <Chip
              size="small"
              color={skipped.length > 0 ? "warning" : "default"}
              label={`Skipped: ${skipped.length}`}
            />
            <Chip
              size="small"
              color={blockers.length > 0 ? "error" : "default"}
              label={`Blockers: ${blockers.length}`}
            />
          </Box>
        )}

        {!batchName && (
          <Typography variant="body2" color="text.secondary">
            Select a batch to see exactly which roll and GR numbers will be
            assigned.
          </Typography>
        )}

        {previewing && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Building preview…</Typography>
          </Box>
        )}

        {previewError && <Alert severity="error">{previewError}</Alert>}

        {unpaidPlannedWithoutOverride && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Unpaid applications can only be admitted after enabling the unpaid
            override and entering a reason.
          </Alert>
        )}

        {blockers.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>Cannot admit yet</AlertTitle>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {blockers.map((b, i) => (
                <li key={i}>{formatServerIssue(b)}</li>
              ))}
            </ul>
          </Alert>
        )}

        {warnings.length > 0 && (
          <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
            <AlertTitle>Check before continuing</AlertTitle>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {warnings.map((w, i) => (
                <li key={i}>{formatServerIssue(w)}</li>
              ))}
            </ul>
          </Alert>
        )}

        {plan.length > 0 && (
          <>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                mb: 1.5,
                alignItems: "center",
              }}
            >
              <Chip
                size="small"
                label={`Roll ${preview.rollRange?.[0]} – ${preview.rollRange?.[1]}`}
              />
              {preview.grRange && (
                <Chip
                  size="small"
                  label={`GR ${preview.grRange[0]} – ${preview.grRange[1]}`}
                />
              )}
              {preview.academicYear && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={preview.academicYear}
                />
              )}
              {unpaidOverrideCount > 0 && (
                <Chip
                  size="small"
                  color="warning"
                  label={`${unpaidOverrideCount} unpaid override(s)`}
                />
              )}
              {skipped.length > 0 && (
                <Chip
                  size="small"
                  color="default"
                  label={`${skipped.length} skipped`}
                />
              )}
              <Chip
                size="small"
                variant="outlined"
                color={sendEmail ? "success" : "default"}
                label={sendEmail ? "Emails will be sent" : "No emails"}
              />
            </Box>

            <Typography variant="caption" color="text.secondary">
              Ordered by surname. Numbers continue from what this batch already
              holds.
            </Typography>

            <TableContainer
              sx={{
                maxHeight: 340,
                mt: 1,
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Roll No</TableCell>
                    <TableCell>GR No</TableCell>
                    <TableCell>Surname</TableCell>
                    <TableCell>First Name</TableCell>
                    <TableCell>App No</TableCell>
                    <TableCell>Application Status</TableCell>
                    <TableCell>Stage</TableCell>
                    <TableCell>Class</TableCell>
                    <TableCell>Payment</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {plan.map((p, i) => {
                    const paymentStatus = normalizePaymentStatus(
                      p.paymentStatus,
                    );
                    const isUnpaidOverride = paymentStatus === "unpaid";
                    return (
                      <TableRow
                        key={p.applicationId}
                        hover
                        sx={
                          isUnpaidOverride
                            ? { backgroundColor: "#fffbeb" }
                            : undefined
                        }
                      >
                        <TableCell>{i + 1}</TableCell>
                        <TableCell sx={{ fontFamily: "monospace" }}>
                          {p.rollNumber}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "monospace" }}>
                          {p.grNumber ?? "—"}
                        </TableCell>
                        <TableCell>{p.lastName}</TableCell>
                        <TableCell>{p.firstName}</TableCell>
                        <TableCell>{p.applicationNumber}</TableCell>
                        <TableCell>{p.applicationStatus || "—"}</TableCell>
                        <TableCell>{p.stage ?? "—"}</TableCell>
                        <TableCell>{p.batchName}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={paymentStatus}
                            color={paymentChipColor(paymentStatus)}
                            variant={
                              paymentStatus === "paid" ? "filled" : "outlined"
                            }
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {skipped.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Skipped ({skipped.length})
            </Typography>
            <TableContainer
              sx={{
                maxHeight: 180,
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>App No</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {skipped.map((s) => (
                    <TableRow key={String(s.applicationId)}>
                      <TableCell>{s.applicationNumber}</TableCell>
                      <TableCell>{s.name}</TableCell>
                      <TableCell sx={{ color: "#b45309" }}>
                        {s.reason}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {hasCurrentPreview && plan.length === 0 && blockers.length === 0 && (
          <Alert severity="info">
            Nothing to admit — every selected application was skipped.
          </Alert>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: "16px 24px",
          backgroundColor: "#f7fafc",
          borderTop: "1px solid #e2e8f0",
        }}
      >
        <Button onClick={onClose} variant="outlined" disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!canAdmit}
          sx={{
            backgroundColor: "#15803d",
            "&:hover": { backgroundColor: "#166534" },
          }}
        >
          {submitting ? "Admitting…" : `Admit ${plan.length || ""}`.trim()}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdmitStudentsDialog;
